import { useEffect, useState } from 'react'
import QRCode from 'qrcode'

/**
 * The QR code on an RKSV receipt.
 *
 * Not decoration: § 11 RKSV requires the machine-readable code on the receipt, and it
 * is what the BMF *Belegcheck* app scans to verify the signature and the chain. A
 * receipt without it cannot be checked, which means the landlord cannot complete the
 * one step that proves their register is set up correctly.
 *
 * Rendered as SVG rather than canvas so that printing it stays sharp — a QR that
 * survives on screen but blurs on paper is the same as no QR at all.
 *
 * Error correction is deliberately M: the payload is long, L would be smaller but
 * less tolerant of a crease or a thermal printer running low, and this code is
 * scanned off paper by a phone.
 */
export function QrCode({
  value, size = 128, className,
}: {
  value: string
  size?: number
  className?: string
}) {
  const [svg, setSvg] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    if (!value) { setSvg(null); return }
    QRCode.toString(value, {
      type: 'svg',
      errorCorrectionLevel: 'M',
      margin: 0,
      width: size,
    })
      .then((out) => { if (alive) { setSvg(out); setFailed(false) } })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [value, size])

  if (failed) {
    // Say so rather than showing a blank square. A missing QR is a compliance
    // problem, and it must not look like a rendering quirk.
    return (
      <div className={className} style={{ width: size }}>
        <p className="text-[9px] text-danger break-all">QR: {value.slice(0, 40)}…</p>
      </div>
    )
  }
  if (!svg) return <div className={className} style={{ width: size, height: size }} />

  return (
    <div
      className={className}
      style={{ width: size, height: size }}
      // The library returns a complete SVG document for a value we produced.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  )
}
